import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CaretRight from "@us/Assets/Icons/CaretRight";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductListActionsStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    view: state.pages.productList.view
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasHistory;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    productDetailsPriceText: TypographyPresentationProps;
    optionsLink?: LinkPresentationProps;
    addToCartButtonIcon?: IconPresentationProps;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
            margin-bottom: -10px;
            margin-left: -10px;
            margin-right: -10px;
            button {
                border: none !important;
            }
            button:active,
            button:focus {
                border: 3px solid #09f !important;
                outline: none;
            }
        `,
    },
    addToCartButton: {
        css: css`
            width: 100%;
            margin: 0 -1px -1px 0;
            font-size: 14px;
            font-weight: 600;
            text-transform: uppercase;
            span {
                margin-left: 5px;
            }
        `,
        typographyProps: {
            transform: "uppercase",
            size: 14,
            css: css`
                letter-spacing: 1px;
                font-size: 14px;
            `,
        },
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                width: 5rem;
                min-width: 5rem;
                margin: 0 0 -1px -1px;
                label {
                    display: none;
                }
                input {
                    border: 1px solid ${getColor("common.border")} !important;
                    text-align: center;
                    font-weight: 700;
                }
                input:active,
                input:focus {
                    border: 3px solid #09f !important;
                    z-index: 2;
                }
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
        `,
    },
    productDetailsPriceText: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
        `,
        size: 13,
        transform: "uppercase",
        weight: 700,
        color: "#2b2b2e",
    },
    price: {
        price: {
            priceText: {
                color: "text.main",
                size: 20,
            },
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
    },
    addToCartButtonIcon: {
        color: "white",
        size: 10,
        css: css`
            margin-left: 7px;
            svg {
                fill: #ffffff;
            }
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    view,
    showPrice,
    showAddToList,
    history,
    extendedStyles,
}) => {
    const isConfigProduct =
        productContext.product.configurationType !== "None" && productContext.product.configurationType !== undefined;
    const showOptionsButton = productContext.product.isVariantParent || isConfigProduct;

    const onClickRouteHandler = () => {
        history.push(productContext.productInfo.productDetailPath);
    };

    const addToCartWrappStyle = extendedStyles?.addToCartWrapper ?? styles.addToCartWrapper;

    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && !showOptionsButton && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...addToCartWrappStyle}>
                {showOptionsButton ? (
                    <Button
                        {...styles.addToCartButton}
                        onClick={onClickRouteHandler}
                        data-test-selector="productDescriptionLink"
                    >
                        {translate("Select Options")}
                    </Button>
                ) : (
                    <Button
                        {...styles.addToCartButton}
                        onClick={onClickRouteHandler}
                        data-test-selector="productDescriptionLink"
                    >
                        {translate("Learn More")}
                        <ButtonIcon src={CaretRight} size={14}/>
                    </Button>
                )}
            </StyledWrapper>
            {showAddToList && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withProductContext(ProductListActions)));
